import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from "../components/SEO";
import Footer from "../components/Footer";
import apiUrl from '../components/Url';
import BeicaSaKutijama from '../images/BeicaSaKutijama.png';
import { useLanguage } from '../i18n/translations';

export default function RadioniceKeramike({ categories }) {
    const { translate } = useLanguage();
    const [selectedImage, setSelectedImage] = useState(null);
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [showReservationModal, setShowReservationModal] = useState(false);
    const [selectedWorkshop, setSelectedWorkshop] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [workshops, setWorkshops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Gallery images
    const galleryImages = [
        {
            src: "/images/radionica/IMG_7615.webp",
            alt: "Radionica keramike",
            title: ""
        },
        {
            src: "/images/radionica/IMG_7633.webp", 
            alt: "Radionica keramike",
            title: ""
        },
        {
            src: "/images/radionica/IMG_7588.webp",
            alt: "Radionica keramike",
            title: ""
        },
        {
            src: "/images/radionica/IMG_7604.webp",
            alt: "Radionica keramike",
            title: ""
        },
        {
            src: "/images/radionica/IMG_7608.webp",
            alt: "Radionica keramike",
            title: ""
        },
        {
            src: "/images/radionica/IMG_7635.webp",
            alt: "Radionica keramike",
            title: ""
        }
    ];

    // Fetch workshops from API
    useEffect(() => {
        const fetchWorkshops = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${apiUrl}/api/workshops`);
                const data = await response.json();
                
                if (data.success) {
                    setWorkshops(data.workshops);
                } else {
                    console.error('Failed to fetch workshops');
                }
            } catch (error) {
                console.error('Error fetching workshops:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchWorkshops();
    }, []);

    const openImageModal = (image) => {
        setSelectedImage(image);
    };

    const closeImageModal = () => {
        setSelectedImage(null);
    };

    const goToNextImage = () => {
        if (selectedImage) {
            const currentIndex = galleryImages.findIndex(img => img.src === selectedImage.src);
            const nextIndex = (currentIndex + 1) % galleryImages.length;
            setSelectedImage(galleryImages[nextIndex]);
        }
    };

    const goToPreviousImage = () => {
        if (selectedImage) {
            const currentIndex = galleryImages.findIndex(img => img.src === selectedImage.src);
            const previousIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
            setSelectedImage(galleryImages[previousIndex]);
        }
    };

    const handleNotifyMe = (workshop) => {
        setSelectedWorkshop(workshop);
        setShowNotifyModal(true);
    };

    const closeNotifyModal = () => {
        setShowNotifyModal(false);
        setSelectedWorkshop(null);
    };

    const openReservationModal = (workshop, date) => {
        setSelectedWorkshop(workshop);
        setSelectedDate(date);
        setShowReservationModal(true);
    };

    const closeReservationModal = () => {
        setShowReservationModal(false);
        setSelectedWorkshop(null);
        setSelectedDate(null);
    };

    const showSuccessMessage = (message) => {
        setSuccessMessage(message);
        setShowSuccessModal(true);
    };

    const closeSuccessModal = () => {
        setShowSuccessModal(false);
        setSuccessMessage('');
    };

    const handleReservation = (workshopId, date) => {
        openReservationModal(
            workshops.find(w => w.id === workshopId),
            date
        );
    };

    const submitReservation = async (formData) => {
        try {
            const response = await fetch(`${apiUrl}/api/workshops/reserve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    workshop_id: selectedWorkshop.id,
                    workshop_date_id: selectedDate.id,
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.email,
                    phone: formData.phone,
                    note: formData.note
                })
            });

            const data = await response.json();

            if (data.success) {
                const successMsg = `${translate("Reservation successfully sent!")}\n\n${translate("Workshop:")} ${selectedWorkshop.title}\n${translate("Date:")} ${new Date(selectedDate.date).toLocaleDateString('bs-BA')}\n${translate("Time:")} ${selectedDate.time}\n\n${data.message}`;
                showSuccessMessage(successMsg);
                closeReservationModal();
                // Refresh workshops to update available spots
                const workshopsResponse = await fetch(`${apiUrl}/api/workshops`);
                const workshopsData = await workshopsResponse.json();
                if (workshopsData.success) {
                    setWorkshops(workshopsData.workshops);
                }
            } else {
                showSuccessMessage(`${translate("Error: {message}").replace('{message}', data.message)}`);
            }
        } catch (error) {
            console.error('Error submitting reservation:', error);
            showSuccessMessage(translate("An error occurred while sending the reservation. Please try again."));
        }
    };

    const submitNotification = async (formData) => {
        try {
            const response = await fetch(`${apiUrl}/api/workshops/notify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    workshop_id: selectedWorkshop.id,
                    email: formData.email,
                    name: formData.name
                })
            });

            const data = await response.json();

            if (data.success) {
                showSuccessMessage(data.message);
                closeNotifyModal();
            } else {
                showSuccessMessage(`${translate("Error: {message}").replace('{message}', data.message)}`);
            }
        } catch (error) {
            console.error('Error submitting notification:', error);
            showSuccessMessage(translate("An error occurred while signing up for notifications. Please try again."));
        }
    };

    // Keyboard navigation for image modal
    React.useEffect(() => {
        const handleKeyPress = (e) => {
            if (selectedImage) {
                if (e.key === 'ArrowRight') {
                    goToNextImage();
                } else if (e.key === 'ArrowLeft') {
                    goToPreviousImage();
                } else if (e.key === 'Escape') {
                    closeImageModal();
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [selectedImage]);

    return (
        <>
            <style>
                {`
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }
                    
                    @keyframes scaleIn {
                        from {
                            opacity: 0;
                            transform: scale(0.8);
                        }
                        to {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                    
                    @keyframes slideUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px) scale(0.95);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0) scale(1);
                        }
                    }
                    
                    .animate-fadeIn {
                        animation: fadeIn 0.3s ease-out;
                    }
                    
                    .animate-scaleIn {
                        animation: scaleIn 0.3s ease-out;
                    }
                    
                    .animate-slideUp {
                        animation: slideUp 0.3s ease-out;
                    }
                `}
            </style>
        <div className="bg-white min-h-screen">
            <SEO
                title="Radionice keramike - Bulka Ceramics"
                description="Pridružite se našim radionicama keramike. Naučite osnovne tehnike rada sa glinom u toploj atmosferi našeg studija."
                type="website"
                name="Bulka Ceramics"
            />

            {/* Hero Section */}
            <div className="bg-bgSkin py-20">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-5xl font-bold text-brownSkin mb-6">
                            {translate("Ceramic Workshops")}
                        </h1>
                        <div className="w-32 h-1 bg-[#facb8c] mx-auto rounded-full mb-6"></div>
                        <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                            {translate("Discover the magic of creating with your own hands. Our workshops are suited to all ages and experience levels - from beginners to those looking to refine their skills.")}
                        </p>
                    </div>
                </div>
            </div>

            {/* Gallery Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
                            {translate("Gallery from our workshops")}
                        </h2>
                        <div className="w-24 h-1 bg-[#facb8c] mx-auto rounded-full"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {galleryImages.map((image, index) => (
                            <div 
                                key={index}
                                className="group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer transform hover:scale-105 transition-all duration-300"
                                onClick={() => openImageModal(image)}
                            >
                                <img
                                    src={image.src}
                                    alt={image.alt}
                                    className="w-full h-64 object-cover"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <div className="text-white text-center">
                                        <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                                        </svg>
                                        <p className="font-medium">{image.title}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Workshops Locations Section */}
            <section className="py-20 bg-bgSkin">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
                            {translate("Locations and schedule")}
                        </h2>
                        <div className="w-24 h-1 bg-[#facb8c] mx-auto rounded-full"></div>
                    </div>

                    {loading ? (
                        <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brownSkin"></div>
                            <p className="mt-4 text-gray-600">{translate("Loading workshops...")}</p>
                        </div>
                    ) : workshops.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fas fa-tools text-gray-400 text-6xl mb-4"></i>
                            <p className="text-gray-600 text-lg">{translate("No workshops currently available")}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {workshops.map((workshop) => (
                            <div key={workshop.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                                {/* Workshop Image */}
                                <div className="relative h-[24rem]">
                                    <img
                                        src={workshop.image}
                                        alt={workshop.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute top-4 right-4">
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium  ${
                                            workshop.available 
                                                ? (workshop.dates && workshop.dates.length > 0 && workshop.dates.every(date => date.spots === 0)
                                                    ? 'bg-red-500 text-white'
                                                    : 'bg-bgSkin text-black')
                                                : 'bg-gray-500 text-white'
                                        }`}>
                                            {workshop.available
                                                ? (workshop.dates && workshop.dates.length > 0 && workshop.dates.every(date => date.spots === 0)
                                                    ? translate('Fully booked')
                                                    : translate('Open')
                                                )
                                                : translate('Coming soon')}
                                        </span>
                                    </div>
                                </div>

                                {/* Workshop Info */}
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-brownSkin mb-2">
                                        {workshop.title}
                                    </h3>
                                    <div className="flex items-center text-gray-600 mb-3">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                                        </svg>
                                        <span className="text-sm">{workshop.address}</span>
                                    </div>
                                    <p className="text-gray-700 mb-6">{workshop.description}</p>

                                    {/* Price */}
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <span className="text-2xl font-bold text-brownSkin">{workshop.price}</span>
                                            <span className="text-gray-600 ml-2">{translate('per person')}</span>
                                        </div>
                                    </div>

                                    {/* Dates and Booking */}
                                    {workshop.external_booking ? (
                                        <div className="space-y-4">
                                            {workshop.external_booking_instructions && (
                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                    <div className="flex items-start">
                                                        <i className="fas fa-info-circle text-blue-500 text-lg mr-3 mt-1"></i>
                                                        <p className="text-blue-800 text-sm">
                                                            {workshop.external_booking_instructions}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <a
                                                href={workshop.external_booking_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-full bg-bgSkin text-black px-6 py-3 rounded-full font-medium hover:bg-brownSkin transition-colors duration-300 flex items-center justify-center"
                                            >
                                                <i className="fab fa-instagram mr-2"></i>
                                                {workshop.external_booking_text}
                                            </a>
                                        </div>
                                    ) : workshop.available && workshop.dates.length > 0 ? (
                                        <div className="space-y-3">
                                            <h4 className="font-medium text-brownSkin">{translate('Available times:')}</h4>
                                            {workshop.dates.map((date, index) => (
                                                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {new Date(date.date).toLocaleDateString('bs-BA', { 
                                                                weekday: 'long', 
                                                                year: 'numeric', 
                                                                month: 'long', 
                                                                day: 'numeric' 
                                                            })}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            <span className={date.spots > 0 && date.spots < 4 ? "text-red-600" : ""}>
                                                                {date.time} • {date.spots > 0
                                                                    ? date.spots < 4
                                                                        ? date.spots === 1
                                                                            ? translate('1 spot available')
                                                                            : translate('{n} spots available').replace('{n}', date.spots)
                                                                        : translate('Open')
                                                                    : translate('Fully booked')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {date.spots > 0 ? (
                                                        <button
                                                            onClick={() => handleReservation(workshop.id, date)}
                                                            className="bg-brownSkin text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#8b5a32] transition-colors duration-300"
                                                        >
                                                            {translate('Reserve')}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleNotifyMe(workshop)}
                                                            className="bg-gray-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-600 transition-colors duration-300"
                                                        >
                                                            {translate('Notify me when available')}
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleNotifyMe(workshop)}
                                            className="w-full bg-gray-500 text-white px-6 py-3 rounded-full font-medium hover:bg-gray-600 transition-colors duration-300"
                                        >
                                            {translate('Notify me when available')}
                                        </button>
                                    )}
                                </div>
                            </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Info Section */}
            <section className="py-20 bg-white">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-6">
                        {translate("What can you expect?")}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-[#facb8c] rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-brownSkin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-brownSkin mb-2">{translate("Learning basic techniques")}</h3>
                            <p className="text-gray-600">{translate("Shaping, glazing and basic clay working techniques")}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-[#facb8c] rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-brownSkin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-brownSkin mb-2">{translate("Creating your own piece")}</h3>
                            <p className="text-gray-600">{translate("Each participant creates their own unique ceramic piece")}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-[#facb8c] rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-brownSkin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-brownSkin mb-2">{translate("Limited number of spots")}</h3>
                            <p className="text-gray-600">{translate("The number of spots is limited to ensure dedicated attention to each participant")}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Image Modal */}
            {selectedImage && (
                <div 
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fadeIn" 
                    onClick={closeImageModal}
                >
                    <div 
                        className="relative max-w-4xl max-h-full transform transition-all duration-300 ease-out animate-scaleIn"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={selectedImage.src}
                            alt={selectedImage.alt}
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                        />
                        
                        {/* Close Button */}
                        <button
                            onClick={closeImageModal}
                            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-all duration-200 hover:scale-110 transform"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>

                        {/* Previous Arrow */}
                        <button
                            onClick={goToPreviousImage}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black/50 rounded-full p-3 hover:bg-black/70 transition-all duration-200 hover:scale-110"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                            </svg>
                        </button>

                        {/* Next Arrow */}
                        <button
                            onClick={goToNextImage}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black/50 rounded-full p-3 hover:bg-black/70 transition-all duration-200 hover:scale-110"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                            </svg>
                        </button>

                        {/* Image Info */}
                        <div className="absolute bottom-4 left-4 bg-black/70 text-white px-4 py-2 rounded-lg backdrop-blur-sm transform transition-all duration-300">
                            <p className="font-medium">{selectedImage.title}</p>
                        </div>

                        {/* Image Counter */}
                        <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-lg backdrop-blur-sm text-sm">
                            {galleryImages.findIndex(img => img.src === selectedImage.src) + 1} / {galleryImages.length}
                        </div>
                    </div>
                </div>
            )}

            {/* Notify Modal */}
            {showNotifyModal && (
                <div 
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fadeIn"
                    onClick={closeNotifyModal}
                >
                    <div 
                        className="bg-white rounded-2xl p-6 max-w-md w-full transform transition-all duration-300 ease-out shadow-2xl animate-slideUp"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-xl font-bold text-brownSkin mb-4">
                            {translate('Notify me when available')}
                        </h3>
                        <p className="text-gray-700 mb-4">
                            {translate('Location: {name}').replace('{name}', selectedWorkshop?.title || '')}
                        </p>
                        <form 
                            className="space-y-4"
                            onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target);
                                submitNotification({
                                    email: formData.get('email'),
                                    name: formData.get('name')
                                });
                            }}
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {translate('Email address')}
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brownSkin focus:border-transparent transition-all duration-200 focus:scale-105"
                                    placeholder="vasa.email@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {translate('Full name')}
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brownSkin focus:border-transparent transition-all duration-200 focus:scale-105"
                                    placeholder={translate('Your name')}
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeNotifyModal}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 hover:scale-105 transform"
                                >
                                    {translate('Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-brownSkin text-white rounded-lg hover:bg-[#8b5a32] transition-all duration-200 hover:scale-105 transform"
                                >
                                    {translate('Sign me up')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reservation Modal */}
            {showReservationModal && (
                <div 
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fadeIn"
                    onClick={closeReservationModal}
                >
                    <div 
                        className="bg-white rounded-2xl p-6 max-w-lg w-full transform transition-all duration-300 ease-out shadow-2xl animate-slideUp max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-2xl font-bold text-brownSkin mb-6 text-center">
                            {translate('Workshop reservation')}
                        </h3>
                        
                        {/* Workshop Details */}
                        <div className="bg-bgSkin rounded-lg p-4 mb-6">
                            <h4 className="font-semibold text-brownSkin mb-2">{selectedWorkshop?.title}</h4>
                            <div className="text-sm text-gray-600 space-y-1">
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                    </svg>
                                    <span>
                                        {selectedDate && new Date(selectedDate.date).toLocaleDateString('bs-BA', { 
                                            weekday: 'long', 
                                            year: 'numeric', 
                                            month: 'long', 
                                            day: 'numeric' 
                                        })}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                    <span>{selectedDate?.time}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                                    </svg>
                                    <span className="font-medium text-brownSkin">{selectedWorkshop?.price}</span>
                                </div>
                           
                            </div>
                        </div>

                        {/* Reservation Form */}
                        <form 
                            className="space-y-4"
                            onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target);
                                submitReservation({
                                    firstName: formData.get('firstName'),
                                    lastName: formData.get('lastName'),
                                    email: formData.get('email'),
                                    phone: formData.get('phone'),
                                    note: formData.get('note')
                                });
                            }}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translate('First name *')}
                                    </label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brownSkin focus:border-transparent transition-all duration-200"
                                        placeholder={translate('Your name')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {translate('Last name *')}
                                    </label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brownSkin focus:border-transparent transition-all duration-200"
                                        placeholder={translate('Your last name')}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {translate('Email address *')}
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brownSkin focus:border-transparent transition-all duration-200"
                                    placeholder="vasa.email@example.com"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {translate('Phone number *')}
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brownSkin focus:border-transparent transition-all duration-200"
                                    placeholder="+387 XX XXX XXX"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {translate('Note (optional)')}
                                </label>
                                <textarea
                                    name="note"
                                    rows="3"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brownSkin focus:border-transparent transition-all duration-200 resize-none"
                                    placeholder={translate('Special requests or questions...')}
                                ></textarea>
                            </div>

                            {/* Form Actions */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeReservationModal}
                                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
                                >
                                    {translate('Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-6 py-3 bg-brownSkin text-white rounded-lg hover:bg-[#8b5a32] transition-all duration-200 font-medium"
                                >
                                    {translate('Confirm reservation')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <div 
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fadeIn"
                    onClick={closeSuccessModal}
                >
                    <div 
                        className="bg-white rounded-2xl p-6 max-w-md w-full transform transition-all duration-300 ease-out shadow-2xl animate-slideUp"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-center">
                            <div className="w-[180px] mx-auto mb-4">
                                <img 
                                    src={BeicaSaKutijama} 
                                    alt="Beica sa kutijama" 
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            
                            <h3 className="text-xl font-bold text-brownSkin mb-4">
                                {translate('Notification')}
                            </h3>
                            
                            <div className="text-gray-700 mb-6 text-left">
                                {successMessage.split('\n').map((line, index) => (
                                    <p key={index} className={line.trim() === '' ? 'mb-2' : 'mb-1'}>
                                        {line.trim() === '' ? '\u00A0' : line}
                                    </p>
                                ))}
                            </div>
                            
                            <button
                                onClick={closeSuccessModal}
                                className="w-full bg-brownSkin text-white px-6 py-3 rounded-lg hover:bg-[#8b5a32] transition-all duration-200 font-medium"
                            >
                                {translate('OK')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer categories={categories} />
        </div>
        </>
    );
}
